/**
 * Low-cardinality route tagging + project-id extraction, shared by every error
 * capture path (Sentry's `beforeSend`, PostHog Error Tracking's `before_send`,
 * and the React error boundaries).
 *
 * A raw pathname is a terrible tag: `/project/<uuid>` mints a new tag value per
 * project, so grouping and search degrade as usage grows. Collapsing the
 * dynamic segment keeps one bucket per ROUTE while the id it replaced is kept
 * separately as `project_id` — which is the field triage actually wants, since
 * it joins an exception to the build/publish events for the same project.
 *
 * Pure string math, zero imports — safe to evaluate before SDK init and
 * trivially unit-testable.
 */

/** Dynamic path segments worth collapsing, in the order they are applied. */
const DYNAMIC_SEGMENTS: Array<[RegExp, string]> = [
  [/\/project\/[^/]+/, "/project/:id"],
  [/\/projects\/[^/]+/, "/projects/:id"],
];

/**
 * Normalise a pathname into a low-cardinality route tag, so `/project/<uuid>`
 * and `/projects/<id>` collapse to one bucket instead of exploding the tag.
 * Returns undefined for a missing/empty pathname so callers can omit the tag
 * rather than stamp a meaningless empty string.
 */
export function routeTagFor(
  pathname: string | undefined | null,
): string | undefined {
  if (!pathname) return undefined;
  let tag = pathname;
  for (const [pattern, replacement] of DYNAMIC_SEGMENTS) {
    tag = tag.replace(pattern, replacement);
  }
  return tag;
}

/**
 * The id `routeTagFor` collapses out of a `/project/<id>` pathname, so an
 * exception can be joined to that project's `pipeline_build_failed` /
 * `publish_failed` events. Deliberately reads only the workspace route: the
 * plural `/projects/<id>` list routes are not a project workspace, and
 * everything else has no project at all.
 *
 * Returns null when the path names no project, so the caller stamps an explicit
 * null rather than omitting the key (an absent property and "no project" read
 * the same in a PostHog filter otherwise).
 */
export function projectIdFromPathname(
  pathname: string | undefined | null,
): string | null {
  if (!pathname) return null;
  const match = /\/project\/([^/?#]+)/.exec(pathname);
  const id = match?.[1]?.trim();
  return id ? id : null;
}
